import { Popover as Primitive } from "radix-ui";
import type { ReactNode } from "react";

/**
 * A floating panel anchored to the control that opened it.
 *
 * ## Why Radix and not a `<div>` with a click-outside handler
 *
 * The list of things a popover has to get right is long and none of it is
 * visible until it is wrong: focus moves into the panel on open and back to the
 * trigger on close, Escape and an outside pointer-down both dismiss it, the page
 * behind stops scrolling, everything outside is hidden from a screen reader while
 * it is open, and the panel flips or shifts when the viewport edge would clip it.
 * A hand-rolled version passes review and then traps an operator's keyboard on
 * the one screen nobody tested. This is `@radix-ui/react-popover`'s entire job.
 *
 * ## Why it is styled here rather than taken from a component library
 *
 * The panel is ink on paper with a hairline edge, because that is what
 * `tokens.css` says every surface in this app is. A generic popover arrives
 * rounded, shadowed and grey-bordered, and one shadowed rectangle on a surface
 * built out of rules is more conspicuous than a whole page of them.
 *
 * The edge is a full-strength ink rule, not `--color-rule`. A hairline is enough
 * to separate two regions of one page; a layer floating *over* a dense table
 * needs to read as a different plane, and with no shadow available the only thing
 * left to say that with is the weight of the border.
 */

export function Popover({
  open,
  onOpenChange,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly children: ReactNode;
}) {
  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </Primitive.Root>
  );
}

/**
 * Wraps whatever control opens the panel. `asChild` so the trigger *is* the
 * caller's button rather than a `<button>` wrapping one — nested buttons are
 * invalid HTML and break the keyboard order.
 */
export function PopoverTrigger({ children }: { readonly children: ReactNode }) {
  return <Primitive.Trigger asChild>{children}</Primitive.Trigger>;
}

export function PopoverPanel({
  label,
  children,
  align = "start",
}: {
  /** Named for a screen reader, which cannot see what the panel is anchored to. */
  readonly label: string;
  readonly children: ReactNode;
  readonly align?: "start" | "center" | "end";
}) {
  return (
    <Primitive.Portal>
      <Primitive.Content
        // Portalled to the body: the trigger sits inside a filter grid with its
        // own stacking and `overflow`, and an in-place panel would be clipped by
        // it. The cost is that the panel leaves the trigger's DOM subtree, which
        // is why the accessible name is set explicitly rather than inherited.
        aria-label={label}
        align={align}
        sideOffset={6}
        // Keeps the panel inside the viewport without letting it detach from the
        // control: it may flip above the trigger or slide along it, but never
        // float free of it.
        collisionPadding={12}
        className={[
          "z-50 border border-ink bg-paper-raised p-3 rounded-hairline",
          // The one motion the panel has, and it is directional: it enters from
          // the side it opened on, so the eye is already looking at the trigger
          // it came from. Switched off wholesale under reduced motion by the
          // global rule in `tokens.css`.
          "origin-(--radix-popover-content-transform-origin)",
          "data-[state=open]:animate-[stage-rise_var(--duration-fast)_var(--ease-swiss)]",
        ].join(" ")}
      >
        {children}
      </Primitive.Content>
    </Primitive.Portal>
  );
}
